import { useRef, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';

/** How long a finger rests before it is a long-press, and how far it may wander meanwhile. */
export const LONG_PRESS_MS = 500;
export const LONG_PRESS_SLOP = 8;

/**
 * A finger's long-press, for what a mouse reaches by right-click (the responsive pass, 2026-10-26).
 * Touch only: a mouse and a pen keep their own buttons. The tap that ends a long-press is swallowed,
 * so holding a card does not also open it.
 */
export function useLongPress<T extends HTMLElement>(onHold: (element: T) => void) {
  const timer = useRef(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const holdRef = useRef(onHold);
  holdRef.current = onHold;
  const cancel = () => {
    window.clearTimeout(timer.current);
    timer.current = 0;
    start.current = null;
  };
  return {
    onPointerDown(event: ReactPointerEvent<T>) {
      fired.current = false;
      if (event.pointerType !== 'touch') return;
      const element = event.currentTarget;
      start.current = { x: event.clientX, y: event.clientY };
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        timer.current = 0;
        fired.current = true;
        holdRef.current(element);
      }, LONG_PRESS_MS);
    },
    onPointerMove(event: ReactPointerEvent<T>) {
      const from = start.current;
      if (from && Math.hypot(event.clientX - from.x, event.clientY - from.y) > LONG_PRESS_SLOP) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onClickCapture(event: ReactMouseEvent<T>) {
      if (!fired.current) return;
      fired.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
    // The browser's own long-press menu (a link's, an image's) would cover ours.
    onContextMenu(event: ReactMouseEvent<T>) {
      if (start.current || fired.current) event.preventDefault();
    },
  };
}
